import { useIntl } from 'react-intl';
import type { FieldErrors, FieldValues, Path, UseFormRegister } from 'react-hook-form';
import FieldLabel from '@/shared/components/FieldLabel';
import FieldError from '@/shared/components/FieldError';
import { inputClass } from '@/shared/utils/styles';
import { hasFieldError, type ServerErrors } from '@/shared/utils/fieldErrors';

interface Props<T extends FieldValues> {
  register: UseFormRegister<T>;
  errors: FieldErrors;
  serverErrors: ServerErrors;
}

export default function PasswordFields<T extends { password: string; passwordConfirmation: string }>({
  register,
  errors,
  serverErrors,
}: Props<T>) {
  const intl = useIntl();

  return (
    <>
      <div>
        <FieldLabel htmlFor="password" labelId="auth.signUp.passwordLabel" />
        <input
          id="password"
          type="password"
          autoComplete="new-password"
          className={inputClass(hasFieldError('password', errors, serverErrors))}
          placeholder={intl.formatMessage({ id: 'auth.signUp.passwordPlaceholder' })}
          {...register('password' as Path<T>, { required: true })}
        />
        <FieldError field="password" errors={errors} serverErrors={serverErrors}
          messages={{ required: 'auth.signUp.passwordRequired' }} />
      </div>

      <div>
        <FieldLabel htmlFor="passwordConfirmation" labelId="auth.signUp.confirmPasswordLabel" />
        <input
          id="passwordConfirmation"
          type="password"
          autoComplete="new-password"
          className={inputClass(hasFieldError('passwordConfirmation', errors, serverErrors))}
          placeholder={intl.formatMessage({ id: 'auth.signUp.confirmPasswordPlaceholder' })}
          {...register('passwordConfirmation' as Path<T>, {
            required: true,
            validate: (value, { password }) => value === password,
          })}
        />
        <FieldError field="passwordConfirmation" errors={errors} serverErrors={serverErrors}
          messages={{ required: 'auth.signUp.confirmPasswordRequired', validate: 'auth.signUp.passwordMismatch' }} />
      </div>
    </>
  );
}
